(() => {
  "use strict";

  class ProductMediaSwiper {
    constructor(gallery) {
      this.gallery = gallery;
      this.mainElement = gallery.querySelector('[id^="Media-Gallery"]');
      this.thumbnailElement = gallery.querySelector(
        '[id^="GalleryThumbnails"]',
      );
      this.pagination = gallery.querySelector("[data-media-pagination]");
      this.originalTabIndex =
        this.mainElement?.getAttribute("tabindex") || null;
      this.originalDirection = this.mainElement?.getAttribute("dir") || null;
      this.originalThumbnailDirection =
        this.thumbnailElement?.getAttribute("dir") || null;
      this.main = null;
      this.thumbnails = null;
      this.mobileBreakpoint = window.matchMedia("(max-width: 767px)");
      this.handleBreakpointChange = this.handleBreakpointChange.bind(this);
      this.handleThumbnailClick = this.handleThumbnailClick.bind(this);
      this.connected = false;
      this.syncing = false;
      this.reducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      ).matches;
    }

    get activeMediaId() {
      const slides = this.visibleSlides();
      return slides[this.main?.activeIndex || 0]?.dataset.mediaId || null;
    }

    shouldMount() {
      const layout = this.mainElement?.dataset.mediaLayout || "carousel";
      return layout === "carousel" || this.mobileBreakpoint.matches;
    }

    visibleSlides() {
      if (!this.mainElement) return [];
      return [
        ...this.mainElement.querySelectorAll(
          ":scope > .swiper-wrapper > .f-product__media",
        ),
      ];
    }

    visibleThumbnails() {
      if (!this.thumbnailElement) return [];
      return [
        ...this.thumbnailElement.querySelectorAll(
          ":scope > .swiper-wrapper > .f-product__media-thumbnails-item",
        ),
      ];
    }

    connect() {
      if (!this.connected) {
        if (this.mobileBreakpoint.addEventListener) {
          this.mobileBreakpoint.addEventListener(
            "change",
            this.handleBreakpointChange,
          );
        } else {
          this.mobileBreakpoint.addListener?.(this.handleBreakpointChange);
        }
        this.thumbnailElement?.addEventListener(
          "click",
          this.handleThumbnailClick,
        );
        this.connected = true;
      }
    }

    handleBreakpointChange() {
      this.gallery.refresh();
    }

    handleThumbnailClick(event) {
      const thumbnail = event.target.closest(
        ".f-product__media-thumbnails-item",
      );
      if (!thumbnail || !this.thumbnailElement?.contains(thumbnail)) return;
      this.gallery.selectMedia(thumbnail.dataset.mediaId);
    }

    mount() {
      if (this.main || !this.mainElement || !this.shouldMount()) return false;

      if (typeof window.Swiper !== "function") return false;

      try {
        const direction = document.documentElement.dir || "ltr";
        this.mainElement.tabIndex = 0;
        this.mainElement.dir = direction;
        if (this.thumbnailElement) this.thumbnailElement.dir = direction;
        this.main = new window.Swiper(this.mainElement, {
          a11y: { enabled: true, slideRole: "listitem" },
          allowTouchMove: true,
          keyboard: { enabled: true, onlyInViewport: true },
          loop: false,
          noSwipingSelector:
            "video, iframe, model-viewer, deferred-media, product-model, .shopify-model-viewer-ui",
          pagination: this.pagination
            ? { el: this.pagination, clickable: true }
            : false,
          slidesPerView: 1,
          speed: this.reducedMotion ? 0 : 300,
          watchOverflow: true,
          on: {
            slideChange: (instance) => {
              if (this.syncing) return;
              const slide = this.visibleSlides()[instance.activeIndex];
              if (slide) this.gallery.handleMediaChange(slide.dataset.mediaId);
            },
          },
        });
        if (this.thumbnailElement) {
          this.thumbnails = new window.Swiper(this.thumbnailElement, {
            a11y: { enabled: true },
            freeMode: true,
            loop: false,
            slidesPerView: 5,
            spaceBetween: 6,
            speed: this.reducedMotion ? 0 : 300,
            watchOverflow: true,
          });
        }
        this.gallery.classList.add("is-enhanced");
        return true;
      } catch (error) {
        console.warn("[media-gallery] Swiper initialization failed", error);
        this.unmount();
        return false;
      }
    }

    selectMedia(mediaId, { instant = false } = {}) {
      const id = String(mediaId);
      const index = this.visibleSlides().findIndex(
        (slide) => slide.dataset.mediaId === id,
      );
      if (index < 0) return false;

      this.syncing = true;
      try {
        if (this.main?.slideTo) {
          this.main.slideTo(index, instant || this.reducedMotion ? 0 : undefined);
        }
        this.thumbnails?.slideTo?.(
          index,
          instant || this.reducedMotion ? 0 : undefined,
        );
      } finally {
        this.syncing = false;
      }
      return true;
    }

    refresh() {
      if (!this.shouldMount()) {
        this.unmount();
        return;
      }
      if (!this.main) {
        this.mount();
        return;
      }
      this.syncing = true;
      try {
        this.main.update?.();
        this.thumbnails?.update?.();
      } finally {
        this.syncing = false;
      }
    }

    restoreAttribute(element, name, value) {
      if (!element) return;
      if (value === null) {
        element.removeAttribute(name);
      } else {
        element.setAttribute(name, value);
      }
    }

    cleanElement(element, slides) {
      if (!element) return;
      element.classList.remove(
        "swiper-initialized",
        "swiper-horizontal",
        "swiper-backface-hidden",
      );
      element.style.removeProperty("cursor");
      const wrapper = element.querySelector(":scope > .swiper-wrapper");
      for (const property of ["transform", "transition-duration"]) {
        wrapper?.style.removeProperty(property);
      }
      slides.forEach((slide) => {
        slide.removeAttribute("aria-hidden");
        slide.removeAttribute("inert");
        for (const property of ["margin-left", "margin-right", "width"]) {
          slide.style.removeProperty(property);
        }
      });
    }

    cleanGeneratedStyles() {
      if (!this.mainElement) return;
      if (this.originalTabIndex === null) {
        this.mainElement.removeAttribute("tabindex");
      } else {
        this.mainElement.setAttribute("tabindex", this.originalTabIndex);
      }
      this.restoreAttribute(
        this.mainElement,
        "dir",
        this.originalDirection,
      );
      this.restoreAttribute(
        this.thumbnailElement,
        "dir",
        this.originalThumbnailDirection,
      );
      this.cleanElement(this.mainElement, this.visibleSlides());
      this.cleanElement(this.thumbnailElement, this.visibleThumbnails());
    }

    unmount() {
      this.main?.destroy?.(true, true);
      this.thumbnails?.destroy?.(true, true);
      this.main = null;
      this.thumbnails = null;
      this.gallery.classList.remove("is-enhanced");
      this.cleanGeneratedStyles();
    }

    destroy() {
      if (this.connected) {
        if (this.mobileBreakpoint.removeEventListener) {
          this.mobileBreakpoint.removeEventListener(
            "change",
            this.handleBreakpointChange,
          );
        } else {
          this.mobileBreakpoint.removeListener?.(this.handleBreakpointChange);
        }
        this.thumbnailElement?.removeEventListener(
          "click",
          this.handleThumbnailClick,
        );
        this.connected = false;
      }
      this.unmount();
    }
  }

  window.ProductMediaSwiper = ProductMediaSwiper;
})();
